import type { FC } from 'react';
import { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import TeamAvatar from 'app/components/TeamAvatar';
import ConfigContext from 'app/common/ConfigContext';
import { formatDate } from 'app/utils/date';
import { getMatchType } from 'app/utils/entities/match_data';
import { getMapRecapText } from 'app/components/MatchesRecap/utils';

import style from './style.module.scss';

interface Props {
  data: NameBlockMatch;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  matchStatus: EventStatus;
  opponent1Wins: number;
  opponent2Wins: number;
  currentGamePosition: number | undefined;
  // Live only — undefined otherwise. The side label is the sole radiant/dire
  // signal for dota2, and the sole CT/T signal for cs-go.
  team1Side?: Side | null;
  team2Side?: Side | null;
  goldLeadIndex: 0 | 1 | null;
  goldLeadLabel: string | null;
  compact?: boolean;
}

const TeamInfos: FC<Props> = ({
  data,
  opponent1,
  opponent2,
  matchStatus,
  opponent1Wins,
  opponent2Wins,
  currentGamePosition,
  team1Side,
  team2Side,
  goldLeadIndex,
  goldLeadLabel,
  compact = false
}) => {
  const { t } = useTranslation();
  const { lang } = useContext(ConfigContext);

  const score = `${opponent1Wins}:${opponent2Wins}`;

  const renderCenter = () => {
    if (matchStatus === 'not_started') {
      const startTime = data.begin_at
        ? formatDate(new Date(data.begin_at), lang, { hour: '2-digit', minute: '2-digit' })
        : t('all-versus');
      const boLabel = getMatchType(data);
      return (
        <div className={style.centerColumn}>
          <span className={style.startTime}>{startTime}</span>
          {boLabel && <span className={style.subLabel}>{boLabel}</span>}
        </div>
      );
    }
    if (matchStatus === 'finished') {
      return <span className={style.score}>{score}</span>;
    }
    const gameNumber = currentGamePosition
      ? t(getMapRecapText(data.videogame_slug), { gameNumber: currentGamePosition })
      : '';
    return (
      <div className={style.centerColumn}>
        <span className={style.score}>{score}</span>
        {gameNumber && <span className={style.subLabel}>{gameNumber}</span>}
      </div>
    );
  };

  // Empty for the sides no grad widget labels yet (lol) — the caller only ever
  // passes a side for dota2 and cs-go, and an empty label hides the tag.
  const sideLabel = (side: Side): string => {
    switch (side) {
      case 'radiant':
        return t('vg-dota2-side-radiant', { defaultValue: 'Radiant' });
      case 'dire':
        return t('vg-dota2-side-dire', { defaultValue: 'Dire' });
      case 'counter-terrorist':
        return t('vg-csgo-side-counter-terrorist', { defaultValue: 'CT Side' });
      case 'terrorist':
        return t('vg-csgo-side-terrorist', { defaultValue: 'T Side' });
      default:
        return '';
    }
  };

  const renderTeam = (
    opponent: OpponentBase | undefined,
    side: Side | null | undefined,
    index: 0 | 1
  ) => {
    const colorClass = index === 0 ? style.team1Color : style.team2Color;
    const logoColorClass = index === 0 ? style.logo_team1 : style.logo_team2;
    const showGold = goldLeadIndex === index && Boolean(goldLeadLabel);
    const showSideRow = Boolean(side) || showGold;

    return (
      <div className={classNames(style.team, { [style.teamRight]: index === 1 })}>
        <TeamAvatar team={opponent} className={classNames(style.logo, logoColorClass)} />
        <div className={style.details}>
          <span className={style.name}>{opponent?.name ?? t('all-team_name_tbd')}</span>
          {showSideRow && (
            <div className={style.sideRow}>
              {side && (
                <span className={style.sideTag}>
                  <span className={classNames(style.sideSquare, colorClass)} />
                  {sideLabel(side)}
                </span>
              )}
              {showGold && (
                <span className={classNames(style.goldBadge, colorClass)}>{goldLeadLabel}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classNames(style.infos, { [style.compact]: compact })}>
      {renderTeam(opponent1, team1Side, 0)}
      <div className={style.center}>{renderCenter()}</div>
      {renderTeam(opponent2, team2Side, 1)}
    </div>
  );
};

TeamInfos.displayName = 'TeamInfos';

export default TeamInfos;
